import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

import { tr } from "../i18n";
export default function AuthModal({ open, onClose }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setError("");
      setForm({ email: "", password: "" });
    }
  }, [open]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(form.email, form.password);
      onClose();
      navigate("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.detail || tr("Something went wrong. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden gap-0 border-0">
        <div className="bg-[#0b1f3a] px-7 pt-7 pb-6">
          <DialogHeader>
            <p className="text-[#c2a15a] text-[11px] tracking-[0.25em] uppercase mb-2">
              {tr("R&H Savings Portal")}
            </p>
            <DialogTitle className="font-display text-white text-[26px] leading-tight">
              {tr("Welcome back")}
            </DialogTitle>
            <DialogDescription className="text-white/60 text-[14px] mt-1">
              {tr("Sign in to view your account overview and portfolio.")}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-7 py-6">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <span className="block text-[13px] font-medium text-[#0b1f3a]/70 mb-1.5">
                {tr("Email")}
              </span>
              <input
                required
                data-testid="login-email-input"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder={tr("jane@company.com")}
                className="rh-input"
              />
            </div>
            <div>
              <span className="block text-[13px] font-medium text-[#0b1f3a]/70 mb-1.5">
                {tr("Password")}
              </span>
              <input
                required
                data-testid="login-password-input"
                type="password"
                minLength={6}
                value={form.password}
                onChange={update("password")}
                placeholder="••••••••"
                className="rh-input"
              />
            </div>

            {error && (
              <p className="text-[13px] text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={busy}
              className="group w-full inline-flex items-center justify-center gap-2 bg-[#c2a15a] hover:bg-[#d8bd82] disabled:opacity-60 text-[#0b1f3a] font-semibold uppercase tracking-wide text-[14px] px-6 py-4 rounded-full transition-colors"
            >
              {busy ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  {tr("Sign in")}
                  <ArrowRight
                    size={17}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </>
              )}
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
